import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Clock, AlertTriangle, Sparkles, X } from "lucide-react";

export default function TrialBanner({ user, onDismiss }) {
  const navigate = useNavigate();
  const [daysLeft, setDaysLeft] = useState(null);
  const [isExpired, setIsExpired] = useState(false);
  const [dismissed, setDismissed] = useState(false);

  useEffect(() => {
    if (user?.trial_end_date) {
      const endDate = new Date(user.trial_end_date);
      const now = new Date();
      const diffTime = endDate - now;
      const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
      
      setDaysLeft(diffDays);
      setIsExpired(diffDays <= 0);
    }
  }, [user]);

  if (!user?.is_trial || dismissed) return null;

  // Trial expired
  if (isExpired) {
    return (
      <div className="bg-gradient-to-r from-red-600 to-rose-600 text-white px-4 py-3">
        <div className="max-w-7xl mx-auto flex flex-col sm:flex-row items-center justify-between gap-3">
          <div className="flex items-center gap-3">
            <AlertTriangle className="w-5 h-5 animate-pulse" />
            <div>
              <p className="font-semibold">Your free trial has ended</p>
              <p className="text-red-100 text-sm">Subscribe now to continue using There You Are</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Button
              size="sm"
              className="bg-white text-red-600 hover:bg-red-50 font-semibold"
              onClick={() => navigate("/subscriptions")}
            >
              Subscribe Now - $59/vehicle
            </Button>
          </div>
        </div>
      </div>
    );
  }

  // Trial ending soon (3 days or less)
  if (daysLeft <= 3) {
    return (
      <div className="bg-gradient-to-r from-amber-500 to-orange-500 text-white px-4 py-3">
        <div className="max-w-7xl mx-auto flex flex-col sm:flex-row items-center justify-between gap-3">
          <div className="flex items-center gap-3">
            <Clock className="w-5 h-5" />
            <div>
              <p className="font-semibold">
                {daysLeft === 1 ? "Last day of your free trial!" : `Only ${daysLeft} days left in your free trial`}
              </p>
              <p className="text-amber-100 text-sm">Subscribe to keep all your data and continue service</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Button
              size="sm"
              className="bg-white text-amber-600 hover:bg-amber-50 font-semibold"
              onClick={() => navigate("/subscriptions")}
            >
              Subscribe Now
            </Button>
            <button 
              onClick={() => setDismissed(true)}
              className="p-1 hover:bg-white/20 rounded"
            >
              <X className="w-4 h-4" />
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Normal trial status
  return (
    <div className="bg-gradient-to-r from-cyan-600 to-blue-600 text-white px-4 py-2">
      <div className="max-w-7xl mx-auto flex flex-col sm:flex-row items-center justify-between gap-2">
        <div className="flex items-center gap-2">
          <Sparkles className="w-4 h-4" />
          <p className="text-sm">
            <span className="font-semibold">Free Trial</span> - {daysLeft} days remaining
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button
            size="sm"
            variant="ghost"
            className="text-white hover:bg-white/20 text-xs h-7"
            onClick={() => navigate("/subscriptions")}
          >
            View Plans
          </Button>
          <button 
            onClick={() => setDismissed(true)}
            className="p-1 hover:bg-white/20 rounded"
          >
            <X className="w-3 h-3" />
          </button>
        </div>
      </div>
    </div>
  );
}
